import * as Sentry from "@sentry/react";
import { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { setFollowUpIdForCallTracking } from "../../../../../../../../../../state/actions";
import { EventsDispatchContext } from "../../../../../../../../../context/EventsProvider";
import useService from "../../../../../../../../../hooks/useService";
import Colors from "../../../../../../../../global/Colors";
import ContactControlsNoStyling from "../../../../../../../../global/ContactControlsNoStyling";
import { CONTENT_VIEW_FOLLOWUP } from "../../../FollowUpModal/components/modalcontent/HistoryListView/state/actions";
import { FollowUpDispatchContext } from "../../context/FollowUpContext";
import {
  CONTENT_EDIT_FOLLOWUP,
  setSelectedFollowUp,
  showContent,
  toggleDialog,
} from "../../state/actions";
import DateModel from "../fields/DateModel";
import NameModel from "../fields/NameModel";
import { STATUS_TYPES } from "../types/statusTypes";
import { getColor } from "../utils/getColor";
import { tooltipData } from "../utils/tooltipData";

import Clock from "./Clock/Clock";

const FollowUpItem = ({ id, label, config, actionType, actionTypeList }) => {
  const dispatch = useContext(FollowUpDispatchContext);
  const globalEventDispatch = useContext(EventsDispatchContext);

  const navigate = useNavigate();
  const location = useLocation();

  const followUpFormRepository = useService("repositories/follow-ups-form-repository");

  const handleEditAction = () => {
    showDialog();
    setFollowUpIdForCallTracking(globalEventDispatch, id);
  };

  const handleFollowUpFormRepository = async (urlPath) => {
    try {
      await followUpFormRepository.query({
        urlPath: urlPath.split("/").slice(2).join("/"),
      });
      navigate(`/form/${config.id}`, { state: { returnTo: location } });
    } catch (error) {
      console.error("Error fetching follow-up form:", error);

      const status = error?.status ?? error?.response?.status ?? null;

      if (Number(status) !== 404) {
        Sentry.withScope((scope) => {
          scope.setUser({
            id: String(config?.actingUser?.pk ?? ""),
            username: config?.actingUser?.name ?? "",
          });
          scope.setContext("Form", {
            user_id: String(config?.actingUser?.pk ?? ""),
            username: config?.actingUser?.name ?? "",
            attempted_form_url: config?.formUrl ?? "",
          });
          Sentry.captureException(error);
        });
      }

      handleEditAction();
    }
  };

  const handleEditActionButton = () => {
    const hasFormUrl = typeof config?.formUrl === "string" && config.formUrl.trim() !== "";

    if (hasFormUrl && config?.id) {
      handleFollowUpFormRepository(config.formUrl);
    } else {
      handleEditAction(config);
    }
  };

  const bgShade = 100;
  const defaultShade = 500;

  const getMainColor = (value) => {
    return getColor(value, defaultShade);
  };
  const getBgColor = (value) => {
    return getColor(value, bgShade) + "50";
  };
  const showDialog = () => {
    let dialogContent = CONTENT_VIEW_FOLLOWUP;

    if (config?.status === STATUS_TYPES.PENDING) {
      dialogContent = CONTENT_EDIT_FOLLOWUP;
    }
    showContent(dispatch, dialogContent);
    toggleDialog(dispatch, true);

    setSelectedFollowUp(dispatch, {
      id: id,
      clock: config.clock,
      label: label,
      timeTillDueDate: config.timeTillDueDate,
      status: config.status,
      actionType: config.actionType,
      completedBy: config.completedBy,
      completedAt: config.completedAt,
      updatedBy: config.updatedBy,
      updatedAt: config.updatedAt,
      client: config.client,
      fields: {
        dueDate: config.dueDate,
        detail: config?.detail,
        ...(config?.provider
          ? {
              provider: {
                id: config?.provider?.id,
                name: config?.provider?.name,
              },
            }
          : {}),
      },
    });
  };
  const getPhoneNumber = (actionType, client, provider, lawfirm) => {
    if (actionType === actionTypeList?.client?.key) {
      return client?.phone;
    }
    if (actionType === actionTypeList?.provider?.key) {
      return provider?.phone;
    }
    if (actionType === actionTypeList?.lawFirm?.key) {
      return lawfirm?.phone;
    }
    return "";
  };
  return (
    <div
      className="followup-item grid w-full cursor-pointer grid-cols-12 items-center gap-0 rounded-md border-t-[2px] border-solid border-slate-500 bg-white p-2 font-[500] capitalize text-slate-900 opacity-100 hover:opacity-80"
      style={{
        borderTop: config?.clock
          ? "2px solid " + getMainColor(config?.clock)
          : "2px solid bg-slate-500",
        backgroundColor: config?.clock ? getBgColor(config?.clock) : "bg-slate-100",
      }}
      onClick={handleEditActionButton}
    >
      <div className="align-center col-span-1 text-sm">
        <ContactControlsNoStyling
          number={getPhoneNumber(actionType, config?.client, config?.provider, config?.lawfirm)}
          followUpId={id}
          buttonWidth={"18px"}
          /* smsTitle={"Text Client"}
          smsIconColor={Colors.slate[500]}
          smsBackgroundColor={Colors.white} */
          phoneTitle={"Call Client"}
          phoneIconColor={Colors.slate[500]}
          phoneBackgroundColor={Colors.white}
        />
        {config?.clock && (
          <Clock
            clockValue={config?.clock}
            tooltipTitle={null}
            tooltipDesc={null}
            tooltipObj={tooltipData?.clock_state_tooltip}
            size={1}
          />
        )}
      </div>
      <div className="col-span-3 text-center text-sm">{label && NameModel(label)}</div>
      <div className="col-span-5 text-center text-sm">
        {config?.timeTillDueDate && config?.timeTillDueDate}
      </div>
      <div className="col-span-3 text-end text-sm">
        {config?.dueDate && DateModel(config?.dueDate)}
      </div>
    </div>
  );
};

export default FollowUpItem;
